import React, { useEffect, useState } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { EditorView } from "@codemirror/view";
import {
  useGetQuestionLazyQuery,
  useGetModuleSubmissionsLazyQuery,
  useGetStudentByEmailIdQuery,
  useGetCampusQuery,
  useGetModuleByIdQuery,
} from "../../../graphql/generated.tsx";
import QuestionCard from "../../Assignments/Components/QuestionCard.jsx";
import { IoMdArrowRoundBack } from "react-icons/io";
import { BsQuestionSquare } from "react-icons/bs";
import { RiQuestionAnswerFill } from "react-icons/ri";
import { Tabs } from "flowbite-react";
import { CommandLineIcon } from "@heroicons/react/24/solid";
import { useNavigate, useLocation } from "react-router-dom";
import StudentLayout from "../../Student/Layout/index.jsx";
import NetworkIndicator from "../../Practice/Components/NetworkIndicator.jsx";
import Loader from "../../../components/Loader.jsx";

const SubmissionCodeViewer = ({ code, language }) => {
  const [languageExtension, setLanguageExtension] = useState([]);
  const sourceCode = code || "// No answer submitted";

  useEffect(() => {
    let active = true;
    const loadLanguage = async () => {
      const normalizedLanguage = String(language || "").toUpperCase();
      let extension = null;

      try {
        if (["CPP", "C", "C++"].includes(normalizedLanguage)) {
          const { cpp } = await import("@codemirror/lang-cpp");
          extension = cpp();
        } else if (["PYTHON", "PY"].includes(normalizedLanguage)) {
          const { python } = await import("@codemirror/lang-python");
          extension = python();
        } else if (normalizedLanguage === "JAVA") {
          const { java } = await import("@codemirror/lang-java");
          extension = java();
        } else if (["JAVASCRIPT", "JS"].includes(normalizedLanguage)) {
          const { javascript } = await import("@codemirror/lang-javascript");
          extension = javascript();
        }
      } catch {
        extension = null;
      }

      if (active) setLanguageExtension(extension ? [extension] : []);
    };

    loadLanguage();
    return () => {
      active = false;
    };
  }, [language]);

  const editorTheme = EditorView.theme({
    "&": { fontSize: "15px" },
    ".cm-content": { padding: "14px 0" },
    ".cm-gutters": { fontSize: "13px" },
  });

  return (
    <CodeMirror
      value={sourceCode}
      extensions={[...languageExtension, editorTheme]}
      theme="light"
      editable={false}
      readOnly
      minHeight="220px"
      basicSetup={{
        lineNumbers: true,
        foldGutter: true,
        highlightActiveLine: false,
      }}
      className="submission-code-editor"
    />
  );
};

/* ─── circular-progress helper ─────────────────────────────────────── */
const CircularProgress = ({ completed, total }) => {
  const radius = 30;
  const circumference = 2 * Math.PI * radius;
  const pct = total > 0 ? completed / total : 0;
  const offset = circumference * (1 - pct);

  return (
    <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
      <svg
        width="76"
        height="76"
        viewBox="0 0 76 76"
        aria-label={`Progress: ${completed} of ${total} completed`}
      >
        {/* track */}
        <circle
          cx="38"
          cy="38"
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="6"
        />
        {/* fill — muted sage green instead of vivid #22c55e */}
        <circle
          cx="38"
          cy="38"
          r={radius}
          fill="none"
          stroke="#6db488"
          strokeWidth="6"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 38 38)"
          style={{ transition: "stroke-dashoffset 0.5s ease" }}
        />
        <text
          x="38"
          y="35"
          textAnchor="middle"
          fontSize="14"
          fontWeight="500"
          fill="#111827"
        >
          {completed}
        </text>
        <text
          x="38"
          y="50"
          textAnchor="middle"
          fontSize="10"
          fill="#9ca3af"
        >
          of {total}
        </text>
      </svg>
      <span className="text-xs text-gray-400">completed</span>
    </div>
  );
};

/* ─── chip helper ───────────────────────────────────────────────────── */
const Chip = ({ icon, label, color = "gray" }) => {
  const colorMap = {
    orange: "bg-orange-50 text-orange-700 border-orange-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
    blue:   "bg-blue-50   text-blue-700   border-blue-200",
    red:    "bg-red-50    text-red-600    border-red-200",
    gray:   "bg-gray-100  text-gray-600   border-gray-200",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${colorMap[color]}`}
    >
      {icon}
      {label}
    </span>
  );
};

/* ─── icons (inline svg, no extra deps) ────────────────────────────── */
const IconClock = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);
const IconLayers = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/>
  </svg>
);
const IconList = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
    <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
  </svg>
);

/* ─── completed badge ───────────────────────────────────────────────── */
const CompletedBadge = () => (
  <span
    style={{
      fontSize: "11px",
      fontWeight: 500,
      color: "#3a7a52",
      background: "#edf7f1",
      border: "0.5px solid #b3d9c1",
      borderRadius: "999px",
      padding: "2px 9px",
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      letterSpacing: "0.01em",
    }}
  >
    {/* checkmark */}
    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="#3a7a52" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="2 6 5 9 10 3"/>
    </svg>
    Completed
  </span>
);

/* ─── main component ────────────────────────────────────────────────── */
const ModuleAssignmentDetails = () => {
  const [getModuleSubmissions, { data: submissionsData }] =
    useGetModuleSubmissionsLazyQuery();
  const [expandedSubmissionId, setExpandedSubmissionId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { questionIds, end_date, module, studentId, moduleID } =
    location.state || {};
  const moduleLookupId = module?.id || module?.moduleID || moduleID || "";

  const { data: moduleData } = useGetModuleByIdQuery({
    variables: { id: moduleLookupId },
    skip: !moduleLookupId,
    fetchPolicy: "network-only",
  });

  const user = JSON.parse(localStorage.getItem("user"));
  let campusName =
    location.state?.campusName ||
    user?.campusName ||
    user?.campus_name ||
    "";
  const email = user?.email;

  const { data: studentData } = useGetStudentByEmailIdQuery({
    variables: { email },
    skip: !email,
  });
  const student = studentData?.getStudentByEmailId;
  const campusId = student?.campusId;
  const { data: campusData } = useGetCampusQuery({
    variables: { id: campusId },
    skip: !campusId,
  });
  if (!campusName && campusData?.campus?.name)
    campusName = campusData.campus.name;

  const [Isload, setIsload] = useState(true);
  useEffect(() => {
    setTimeout(() => setIsload(false), 2000);
  }, []);

  /* submissions refetch on focus / custom event */
  useEffect(() => {
    const moduleId =
      module?.id || moduleID || module?._id || module?.moduleID;
    function refetchSubmissions() {
      if (student?.id && moduleId && campusName) {
        getModuleSubmissions({
          variables: {
            studentId: String(student?.id),
            moduleId,
            campusName,
          },
          fetchPolicy: "network-only",
        });
      }
    }
    refetchSubmissions();
    window.addEventListener("moduleSubmissionsDirty", refetchSubmissions);
    if (localStorage.getItem("moduleSubmissionsDirty")) {
      refetchSubmissions();
      localStorage.removeItem("moduleSubmissionsDirty");
    }
    return () =>
      window.removeEventListener(
        "moduleSubmissionsDirty",
        refetchSubmissions
      );
  }, [student, module, moduleID, campusName, getModuleSubmissions]);

  useEffect(() => {
    const moduleId =
      module?.id || moduleID || module?._id || module?.moduleID;
    function refetchSubmissions() {
      if (student?.id && moduleId && campusName) {
        getModuleSubmissions({
          variables: {
            studentId: String(student?.id),
            moduleId,
            campusName,
          },
          fetchPolicy: "network-only",
        });
      }
    }
    window.addEventListener("focus", refetchSubmissions);
    window.addEventListener("moduleSubmissionsDirty", refetchSubmissions);
    if (localStorage.getItem("moduleSubmissionsDirty")) {
      refetchSubmissions();
      localStorage.removeItem("moduleSubmissionsDirty");
    }
    return () => {
      window.removeEventListener("focus", refetchSubmissions);
      window.removeEventListener(
        "moduleSubmissionsDirty",
        refetchSubmissions
      );
    };
  }, [student, module, moduleID, campusName, getModuleSubmissions]);

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchQuestion] = useGetQuestionLazyQuery();

  useEffect(() => {
    let isMounted = true;
    async function fetchQuestions() {
      if (Array.isArray(questionIds) && questionIds.length > 0) {
        setLoading(true);
        const results = await Promise.all(
          questionIds.map(async (id) => {
            try {
              const { data } = await fetchQuestion({ variables: { id } });
              return data?.getQuestion;
            } catch {
              return null;
            }
          })
        );
        if (isMounted) {
          setQuestions(results.filter(Boolean));
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    }
    fetchQuestions();
    return () => {
      isMounted = false;
    };
  }, [questionIds, fetchQuestion]);

  /* formatted due date */
  const formattedDueDate = React.useMemo(() => {
    if (!end_date) return "--/--/----";
    const d = new Date(end_date);
    if (isNaN(d)) return end_date;
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }, [end_date]);

  /* completion count */
  const completedCount = React.useMemo(() => {
    if (!submissionsData?.getModuleSubmissions || !questions.length)
      return 0;
    return questions.filter((q) =>
      submissionsData.getModuleSubmissions.some(
        (s) => s.question_id === q.id && s.is_completed
      )
    ).length;
  }, [submissionsData, questions]);

  const totalQ = Array.isArray(questionIds) ? questionIds.length : 0;
  const moduleDetails = moduleData?.getModuleById || module || {};
  const daywisePlan = moduleDetails?.daywise_plan || [];
  const currentDay =
    daywisePlan.find((day) => day.day_id === location.state?.day_id) ||
    daywisePlan[0] ||
    null;

  if (loading || Isload) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader />
      </div>
    );
  }

  /* ── render ── */
  return (
    <StudentLayout>
      <NetworkIndicator />
      <div className="min-h-screen p-4 bg-white md:p-10">

        {/* Back button */}
        <button
          onClick={() => navigate("/assignments")}
          className="mb-6 text-gray-500 hover:text-gray-700 focus:outline-none"
        >
          <span className="flex items-center gap-2 text-sm font-medium">
            <IoMdArrowRoundBack className="w-5 h-5" />
            Go back
          </span>
        </button>

        {/* ── Single card: header + tabs ──────────────────────────── */}
        <div
          className="overflow-hidden bg-white rounded-2xl"
          style={{
            border: "1px solid #e5e7eb",
            boxShadow: "0 2px 12px 0 rgba(0,0,0,0.07), 0 1.5px 4px 0 rgba(0,0,0,0.04)",
            borderRadius: "1rem",
          }}
        >
          {/* header */}
          <div
            className="px-6 py-5 md:px-7 md:py-6"
            style={{
              borderLeft: "4px solid #3b82f6",
            }}
          >
            <div className="flex items-start justify-between gap-4">

              {/* left: icon + title + desc */}
              <div className="flex items-start flex-1 min-w-0 gap-4">
                <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center mt-0.5">
                  <CommandLineIcon className="w-5 h-5 text-blue-500" />
                </div>

                <div className="min-w-0">
                  <h1 className="text-lg font-semibold text-gray-900 leading-snug mb-1.5 md:text-xl">
                    {currentDay?.name || moduleDetails?.name || "Module name"}
                  </h1>
                  <p className="max-w-[85%] mb-3 text-sm leading-relaxed text-gray-500">
                    {currentDay?.day_description
                      ? currentDay.day_description.length > 272
                        ? currentDay.day_description.substring(0, 272) + "…"
                        : currentDay.day_description
                      : moduleDetails?.short_description
                        ? moduleDetails.short_description.length > 272
                          ? moduleDetails.short_description.substring(0, 272) + "…"
                          : moduleDetails.short_description
                        : "No description available."}
                  </p>
                </div>
              </div>

              {/* right: circular progress ring */}
              <CircularProgress
                completed={completedCount}
                total={totalQ}
              />
            </div>
          </div>

          {/* tabs */}
          <div className="relative px-4 pt-4 pb-4 md:px-8 md:pb-8 md:pt-5">
            {/* chips row — right aligned on same line as tab buttons */}
            <div className="absolute right-4 md:right-8 top-7 md:top-8 flex flex-wrap gap-1.5 md:gap-2">
              {/* <Chip
                icon={<IconClock />}
                label={`Due ${formattedDueDate}`}
                color="red"
              /> */}
              <Chip
                icon={<IconLayers />}
                label={
                  location.state?.moduleDomain ||
                  moduleDetails?.domain ||
                  "—"
                }
                color="purple"
              />
              <Chip
                icon={<IconList />}
                label={`${totalQ} question${totalQ !== 1 ? "s" : ""}`}
                color="blue"
              />
            </div>
            <Tabs aria-label="Module tabs" variant="underline">

            {/* Questions tab */}
            <Tabs.Item title="Questions" icon={BsQuestionSquare}>
              {questions.length === 0 && (
                <div className="p-4 mb-4 text-center border rounded-md bg-gray-50">
                  No questions found.
                </div>
              )}
              <div className="flex flex-col gap-2">
                {questions.map((question, idx) => {
                  let isCompleted = false;
                  if (submissionsData?.getModuleSubmissions) {
                    isCompleted = submissionsData.getModuleSubmissions.some(
                      (sub) =>
                        sub.question_id === question.id && sub.is_completed
                    );
                  }
                  return (
                    <div
                      key={question.id || idx}
                      style={{
                        cursor: "pointer",
                        borderRadius: "0.5rem",
                        overflow: "hidden",
                      }}
                      onClick={() => {
                        if (question?.id) {
                          const courseId =
                            location.state?.moduleID ||
                            (module && module.moduleID) ||
                            "";
                          navigate(
                            `/practice-lab/display-practice-lab/module-assignment-details/${
                              location.state?.clusterId || ""
                            }/${courseId}/${question.id}`,
                            {
                              state: {
                                question,
                                questions,
                                moduleID: location.state?.moduleID,
                                module,
                                studentId,
                                languages:
                                  module?.languages || [
                                    "PYTHON",
                                    "CPP",
                                    "JAVA",
                                    "C",
                                  ],
                                predefinedCode: question?.predefinedCode || {},
                                clusterId: location.state?.clusterId,
                                day_id: location.state?.day_id,
                                isCompleted,
                              },
                            }
                          );
                        }
                      }}
                    >
                      <QuestionCard
                        data={question}
                        isCompleted={isCompleted}
                      />
                    </div>
                  );
                })}
              </div>
            </Tabs.Item>

            {/* Submissions tab */}
            <Tabs.Item title="Submission" icon={RiQuestionAnswerFill}>
              <div className="space-y-4">
                {questions.map((question, index) => {
                  if (!submissionsData?.getModuleSubmissions) return null;
                  const completedSubs =
                    submissionsData.getModuleSubmissions.filter(
                      (s) =>
                        s.question_id === question.id && s.is_completed
                    );
                  if (completedSubs.length === 0) return null;
                  const lastCompleted =
                    completedSubs[completedSubs.length - 1];
                  let code = "";
                  if (
                    Array.isArray(lastCompleted.submissions) &&
                    lastCompleted.submissions.length > 0
                  ) {
                    code = lastCompleted.submissions[0].code;
                  } else {
                    code = lastCompleted.code;
                  }
                  return (
                    <div
                      key={index}
                      className="overflow-hidden border border-gray-200 rounded-xl bg-white cursor-pointer"
                      onClick={() =>
                        setExpandedSubmissionId((currentId) =>
                          currentId === question.id ? null : question.id
                        )
                      }
                    >
                      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-gray-100 border-b border-gray-200">
                        <div className="flex items-center min-w-0 gap-2">
                          <CommandLineIcon className="w-4 h-4 flex-shrink-0 text-gray-600" />
                          <span className="text-sm font-semibold text-gray-700 truncate">
                            Q{index + 1} - {question?.title || question?.name || "Question"}
                          </span>
                        </div>
                        <div className="flex items-center flex-shrink-0 gap-3">
                          <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-mono">
                            {lastCompleted.language || "Unknown"}
                          </span>
                          <span className="text-xs font-semibold text-green-700">
                            Score: {lastCompleted.score ?? 0}
                          </span>
                        </div>
                      </div>
                      {expandedSubmissionId === question.id && (
                        <div className="bg-white">
                          <SubmissionCodeViewer
                            code={code?.trim()}
                            language={lastCompleted.language}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}

                {questions.every((q) => {
                  if (!submissionsData?.getModuleSubmissions) return true;
                  return (
                    submissionsData.getModuleSubmissions.filter(
                      (s) => s.question_id === q.id && s.is_completed
                    ).length === 0
                  );
                }) && (
                  <div className="p-4 mb-4 text-center border rounded-md bg-gray-50">
                    No completed submissions found.
                  </div>
                )}
              </div>
            </Tabs.Item>
          </Tabs>
        </div>
        {/* ── END SINGLE CARD ────────────────────────────────────── */}
      </div>

      </div>
    </StudentLayout>
  );
};

export default ModuleAssignmentDetails;